import React from 'react';
import HeadData from 'components/head-data';
import Sitemap from 'components/sitemap';
import Layout from 'components/layout';
import { headerData } from '.';
import { CountryCode } from 'types/countries';
import { createInsightsApi } from 'data/insights';
import { FeaturesType, useThemeContext, useUser } from 'contexts';
import { createSeoLinksApi } from 'data/seo-links';

import type { SitemapPlace } from 'components/sitemap';
import type { GetServerSidePropsContext } from 'next';
import { createGetServerSideProps, HandlerType } from 'utils/next/shared-getServerSideProps';
import { isExpTenant, isTenantExpCommercial, isTenantExpCommercialCa, TenantTypes } from '@tenants';
import { Namespaces } from 'types/locales';
import { NOT_FOUND } from 'constants/not-found';

const countryToCountryCode = {
  ca: 'CA',
  us: 'US',
};

const handler: HandlerType<SitemapPageProps> = async (context: GetServerSidePropsContext, _features?: FeaturesType, tenant?: TenantTypes) => {
  if (isTenantExpCommercial(tenant.id) || isTenantExpCommercialCa(tenant.id)) {
    return NOT_FOUND;
  }
  const { country, filter } = context.params || {}; // Extract country and filter from URL params
  const seoLinksApi = createSeoLinksApi(createInsightsApi());
  const isCountrySpecificSitemap = isExpTenant(tenant.id);
  const siteCountryLocation = countryToCountryCode[country as keyof typeof countryToCountryCode] as CountryCode;
  const provincesRes = await seoLinksApi.getProvinceLinks({
    country: siteCountryLocation,
    addNameAffixes: false,
  });
  const provinces = provincesRes.links;

  const citiesRes = await seoLinksApi.getCityLinks({
    country: siteCountryLocation,
    limit: 9000,
    addNameAffixes: false,
  });
  const cities = citiesRes.links;

  return {
    props: {
      provinces,
      cities,
      filter: filter as string,
      country: country as string,
      isCountrySpecificSitemap,
    },
  };
};

export const getServerSideProps = createGetServerSideProps<SitemapPageProps>(handler, [
  Namespaces.COMMON,
]);

export type SitemapPageProps = {
  provinces: SitemapPlace[];
  cities: SitemapPlace[];
  filter: string;
  country: string;
  isCountrySpecificSitemap: boolean;
};

export default function SitemapPage({ provinces, cities, filter, country, isCountrySpecificSitemap }: SitemapPageProps) {
  const { theme } = useThemeContext();
  const { siteLocation } = useUser();

  return (
    <Layout>
      <HeadData data={headerData(theme.ogSiteName, siteLocation)} />
      <Sitemap provinces={provinces} cities={cities} filter={filter} country={country} isCountrySpecificSitemap={isCountrySpecificSitemap} />
    </Layout>
  );
}
